import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import type { Status } from "../../../types/deal";
import type { FunnelPeriod } from "../../../types/dealFunnel";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { ErrorDialog } from "../../../components/common/errorDialog";
import { apiGet } from "../../../services/genesisApi/httpsMethods";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import "../../../assets/css/tabs.css";

import { DealAnalysisDealsTable } from "./DealAnalysisDealsTable";
import { DealsByMonthLineChart } from "./DealsByMonthLineChart";
import { DealsByMonthStatusTable } from "./DealsByMonthStatusTable";
import {
  DealStatusBreakdownChart,
  DEAL_ANALYSIS_PANEL_HEIGHT,
} from "./DealStatusBreakdownChart";
import { KillReasonsBreakdownChart } from "./KillReasonsBreakdownChart";
import {
  getKillReasonName,
  isKilledOrLostStatus,
} from "../../../utils/dealStatusHelpers";
import { PeriodToggle } from "./PeriodToggle";

type DealLike = any;

const isAccordionLike = (deal: DealLike): boolean => {
  if (!deal || typeof deal !== "object") return false;
  return (
    typeof deal.deal_id === "number" ||
    typeof deal.accordion_id === "number" ||
    typeof deal.accordion_name === "string"
  );
};

const toValidDate = (value: unknown): Date | null => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value as any);
  return Number.isNaN(d.getTime()) ? null : d;
};

function periodKey(date: Date, period: FunnelPeriod): string {
  const year = date.getFullYear();
  if (period === "year") return String(year);
  if (period === "quarter") {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return `${year}-Q${q}`;
  }
  return `${year}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function periodLabel(date: Date, period: FunnelPeriod): string {
  const year = date.getFullYear();
  if (period === "year") return String(year);
  if (period === "quarter") {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return `Q${q} ${year}`;
  }
  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    year: "numeric",
  }).format(date);
}

function periodTitles(period: FunnelPeriod): {
  chart: string;
  table: string;
} {
  if (period === "year") {
    return {
      chart: "Deals Logged by Year",
      table: "Yearly Deal Summary",
    };
  }
  if (period === "quarter") {
    return {
      chart: "Deals Logged by Quarter",
      table: "Quarterly Deal Summary",
    };
  }
  return {
    chart: "Deals Logged by Month",
    table: "Monthly Deal Summary",
  };
}

type TimeBucket = {
  key: string;
  label: string;
  deals: DealLike[];
  total: number;
  byStatus: Map<string, number>;
};

export function DealAnalysisTab(props: {
  statusIds: number[];
  statusFilter: Status[];
}) {
  const { statusIds } = props;

  const [isDealsLoading, setIsDealsLoading] = useState(false);
  const [period, setPeriod] = useState<FunnelPeriod>("quarter");
  const [activeTabIndex, setActiveTabIndex] = useState(1); // 0 = overview, 1.. = buckets
  const [selectedBucketKey, setSelectedBucketKey] = useState<string | null>(
    null,
  );
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [analysisDeals, setAnalysisDeals] = useState<DealLike[]>([]);

  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  const { status: statusList } = useReferenceLists();

  useEffect(() => {
    setIsDealsLoading(true);
    apiGet("deals?active_only=false")
      .then((response) => {
        if (response.success && response.data) {
          setAnalysisDeals(response.data);
        } else {
          showErrorFromResponse(response);
        }
      })
      .catch(() => {
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to fetch deals for analysis. Please try again.",
        });
      })
      .finally(() => setIsDealsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusIdsKey = useMemo(
    () =>
      statusIds
        .slice()
        .sort((a, b) => a - b)
        .join(","),
    [statusIds.join(",")],
  );

  const filteredDeals = useMemo(() => {
    const idSet = new Set(statusIds);
    return analysisDeals.filter((d) => {
      if (isAccordionLike(d)) return false;
      if (statusIds.length === 0) return true;
      const dealStatusId = d?.status?.id;
      return typeof dealStatusId === "number" && idSet.has(dealStatusId);
    });
  }, [analysisDeals, statusIdsKey]);

  const timeBuckets = useMemo(() => {
    const buckets = new Map<string, TimeBucket>();

    for (const deal of filteredDeals) {
      const created = toValidDate(deal?.created_date);
      if (!created) continue;

      const key = periodKey(created, period);
      const label = periodLabel(created, period);
      const statusName =
        (deal?.status?.status as string | undefined) || "Unknown";

      const bucket =
        buckets.get(key) ??
        (() => {
          const next: TimeBucket = {
            key,
            label,
            deals: [],
            total: 0,
            byStatus: new Map<string, number>(),
          };
          buckets.set(key, next);
          return next;
        })();

      bucket.deals.push(deal);
      bucket.total += 1;
      bucket.byStatus.set(
        statusName,
        (bucket.byStatus.get(statusName) ?? 0) + 1,
      );
    }

    return Array.from(buckets.values()).sort((a, b) =>
      a.key < b.key ? 1 : -1,
    );
  }, [filteredDeals, period]);

  // Joined bucket keys: changes whenever the set/order of buckets changes, even
  // if the count stays the same, so the selection effects below re-validate.
  const timeBucketKeys = useMemo(
    () => timeBuckets.map((bucket) => bucket.key).join("|"),
    [timeBuckets],
  );

  useEffect(() => {
    if (timeBuckets.length === 0) {
      setSelectedBucketKey(null);
      setSelectedStatus(null);
      setActiveTabIndex(0);
      return;
    }
    setSelectedBucketKey((prev) =>
      prev && timeBuckets.some((m) => m.key === prev)
        ? prev
        : timeBuckets[0].key,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeBucketKeys, period]);

  useEffect(() => {
    if (activeTabIndex === 0) return;
    if (!selectedBucketKey) {
      setActiveTabIndex(1);
      return;
    }
    const bucketIdx = timeBuckets.findIndex((m) => m.key === selectedBucketKey);
    setActiveTabIndex(bucketIdx >= 0 ? bucketIdx + 1 : 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBucketKey, timeBucketKeys]);

  useEffect(() => {
    setSelectedStatus(null);
  }, [selectedBucketKey]);

  const selectedBucket = useMemo(
    () => timeBuckets.find((m) => m.key === selectedBucketKey) ?? null,
    [timeBuckets, selectedBucketKey],
  );

  const statusCards = useMemo(() => {
    if (!selectedBucket) return [];
    return Array.from(selectedBucket.byStatus.entries())
      .map(([statusName, count]) => ({
        statusName,
        count,
        pct: selectedBucket.total > 0 ? (count / selectedBucket.total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [selectedBucket]);

  const dealsForTable = useMemo(() => {
    if (!selectedBucket) return [];
    if (!selectedStatus) return selectedBucket.deals;

    return selectedBucket.deals.filter(
      (d) => (d?.status?.status as string | undefined) === selectedStatus,
    );
  }, [selectedBucket, selectedStatus]);

  const killReasonRows = useMemo(() => {
    const includeDeals: DealLike[] = [];

    const counts = new Map<string, number>();
    for (const d of dealsForTable) {
      const killReasonName = getKillReasonName(d);

      if (killReasonName) {
        includeDeals.push(d);
        counts.set(killReasonName, (counts.get(killReasonName) ?? 0) + 1);
        continue;
      }

      const statusName = d?.status?.status;
      if (isKilledOrLostStatus(statusName)) {
        includeDeals.push(d);
        const name = "No kill reason";
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }

    const total = includeDeals.length;
    if (total === 0)
      return {
        total: 0,
        rows: [] as { name: string; count: number; pct: number }[],
      };

    const rows = Array.from(counts.entries())
      .map(([name, count]) => ({ name, count, pct: (count / total) * 100 }))
      .sort((a, b) => b.count - a.count);

    const top = rows.slice(0, 10);
    const rest = rows.slice(10);
    if (rest.length > 0) {
      const restCount = rest.reduce((sum, r) => sum + r.count, 0);
      top.push({
        name: "Other",
        count: restCount,
        pct: (restCount / total) * 100,
      });
    }

    return { total, rows: top };
  }, [dealsForTable]);

  const chartPoints = useMemo(() => {
    return [...timeBuckets]
      .sort((a, b) => (a.key > b.key ? 1 : -1))
      .map((m) => ({ key: m.key, label: m.label, count: m.total }));
  }, [timeBuckets]);

  const bucketsChrono = useMemo(() => {
    return [...timeBuckets].sort((a, b) => (a.key > b.key ? 1 : -1));
  }, [timeBuckets]);

  const statusRowNames = useMemo(() => {
    const totals = new Map<string, number>();
    for (const m of bucketsChrono) {
      for (const [name, count] of m.byStatus.entries()) {
        totals.set(name, (totals.get(name) ?? 0) + count);
      }
    }

    const present = new Set(
      Array.from(totals.entries())
        .filter(([, c]) => c > 0)
        .map(([n]) => n),
    );
    const orderedFromLists = (statusList ?? [])
      .slice()
      .sort((a: any, b: any) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
      .map((s: any) => s?.status)
      .filter(
        (name: any): name is string =>
          typeof name === "string" && present.has(name),
      );

    const extras = Array.from(present)
      .filter((n) => !orderedFromLists.includes(n))
      .sort();
    return [...orderedFromLists, ...extras];
  }, [bucketsChrono, statusList]);

  const showKillReasons =
    selectedStatus != null && isKilledOrLostStatus(selectedStatus);

  const dealsTableTitle = selectedStatus
    ? `Deals in ${selectedBucket?.label ?? ""} — ${selectedStatus}`
    : `Deals in ${selectedBucket?.label ?? ""} — All statuses`;

  const titles = periodTitles(period);

  if (isDealsLoading) {
    return (
      <Box
        sx={{
          padding: "0px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress size={18} />
        <Typography sx={{ fontSize: "14px", color: "#182737" }}>
          Loading deal analysis…
        </Typography>
      </Box>
    );
  }

  if (timeBuckets.length === 0) {
    return (
      <Box sx={{ padding: "0px" }}>
        <Typography sx={{ fontSize: "14px", color: "#182737" }}>
          No deals found to analyze.
        </Typography>
        <ErrorDialog
          open={errorDialog.open}
          onClose={hideError}
          apiResponse={errorDialog.apiResponse}
          customMessage={errorDialog.customMessage}
          title={errorDialog.title}
        />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 0,
          }}
        >
          <Tabs
            value={Math.min(activeTabIndex, timeBuckets.length)}
            onChange={(_, next) => {
              setActiveTabIndex(next);
              if (next === 0) {
                setSelectedBucketKey(null);
                return;
              }
              setSelectedBucketKey(timeBuckets[next - 1]?.key ?? null);
            }}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ className: "tf-tabs__indicator" }}
            sx={{
              flex: 1,
              minWidth: 0,
              borderBottom: "none",
            }}
          >
            <Tab className="tf-tab tf-tab--compact" label="Overview" />
            {timeBuckets.map((m) => (
              <Tab
                key={m.key}
                className="tf-tab tf-tab--compact"
                label={`${m.label} (${m.total})`}
              />
            ))}
          </Tabs>

          <Box sx={{ flexShrink: 0, pb: 0.5 }}>
            <PeriodToggle
              value={period}
              onChange={(next) => {
                setPeriod(next);
                setActiveTabIndex(0);
                setSelectedBucketKey(null);
                setSelectedStatus(null);
              }}
            />
          </Box>
        </Box>

        <Box sx={{ pt: { xs: 2, md: 3 }, pb: 1 }}>
          {activeTabIndex === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <DealsByMonthLineChart
                points={chartPoints}
                title={titles.chart}
              />
              <DealsByMonthStatusTable
                monthsChrono={bucketsChrono}
                statusRowNames={statusRowNames}
                title={titles.table}
              />
            </Box>
          ) : selectedBucket ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: 2,
                  alignItems: "stretch",
                  width: "100%",
                  minHeight: DEAL_ANALYSIS_PANEL_HEIGHT,
                }}
              >
                <Box
                  sx={{
                    flex: { xs: "1 1 auto", lg: "0 1 38%" },
                    maxWidth: { lg: "38%" },
                    minWidth: 0,
                    display: "flex",
                    width: "100%",
                    minHeight: DEAL_ANALYSIS_PANEL_HEIGHT,
                  }}
                >
                  <DealStatusBreakdownChart
                    cards={statusCards}
                    totalDeals={selectedBucket.total}
                    selectedStatus={selectedStatus}
                    onSelectStatus={setSelectedStatus}
                    minHeight={DEAL_ANALYSIS_PANEL_HEIGHT}
                  />
                </Box>

                <Box
                  sx={{
                    flex: { xs: "1 1 auto", lg: "1 1 62%" },
                    minWidth: 0,
                    display: "flex",
                    width: "100%",
                    minHeight: DEAL_ANALYSIS_PANEL_HEIGHT,
                  }}
                >
                  <DealAnalysisDealsTable
                    deals={dealsForTable}
                    title={dealsTableTitle}
                    minHeight={DEAL_ANALYSIS_PANEL_HEIGHT}
                  />
                </Box>
              </Box>

              {showKillReasons ? (
                <KillReasonsBreakdownChart
                  data={killReasonRows}
                  statusLabel={selectedStatus}
                />
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
}
